<!-- eslint-disable ts/consistent-type-definitions -->
<script setup lang='ts' generic="T">
import type { Sizes } from '../../lib/types.ts'
import { IconCaretDown, IconCaretUp, IconMagnifyingGlass, IconX } from '@iconify-prerendered/vue-ph'
import { computed, onMounted, ref, useId, useTemplateRef } from 'vue'
import { searchString } from '../../lib/helpers.ts'
import { viewport } from '../../lib/viewport.ts'
import Button from '../Button/Button.vue'
import Dropdown from '../Dropdown/Dropdown.vue'
import DropdownItem from '../Dropdown/DropdownItem.vue'
import DropdownTitle from '../Dropdown/DropdownTitle.vue'
import Input from '../Input/Input.vue'
import '../Input/input.scss'
import './select.scss'

export type SelectOption = {
  value: any
  label: string
}

type Props = {
  single?: boolean
  readonly?: boolean
  options: SelectOption[]
  label?: string
  placeholder?: string
  required?: boolean
  hint?: string
  search?: boolean
  maxActiveOptions?: number
  showClear?: boolean
  disabled?: boolean
  errors?: string[]
  size?: Sizes
  /**
   * Set he max height of the dropdown element before it starts scrolling
   */
  maxHeight?: number | string
  /**
   * Disable mobile drawer behavior, always use the floating dropdown.
   */
  noMobileDrawer?: boolean
  /**
   * By default, elements with transition already use a default fade transition. This can be replaced by a custom vue transition class name.
   *
   * Setting the value to `none` will not apply any transition. This is useful when using viewTransitions to prevent conflicts between default animation and the view transition
   */
  transitionName?: string | 'none'
}

const {
  readonly,
  required,
  hint,
  placeholder,
  options,
  single = true,
  search,
  maxActiveOptions,
  showClear,
  disabled,
  errors = [] as string[],
  size = 'm',
  noMobileDrawer = false,
  transitionName,
  maxHeight,
} = defineProps<Props>()

const sizeStyle = computed(() => {
  switch (size) {
    case 's': return { '--vui-input-height': '28px', '--vui-input-padding': '4px' }
    case 'l': return { '--vui-input-height': '44px', '--vui-input-padding': '16px' }
    default: return {}
  }
})

const selected = defineModel<SelectOption[] | undefined>()
const triggerRef = useTemplateRef('trigger')

//
function setValue(option: SelectOption) {
  if (single) {
    // Single
    if (selected.value?.some(o => o.value === option.value) && !required) {
      selected.value = undefined
    }
    else {
      selected.value = [option]
    }
  }
  else {
    if (selected.value && selected.value?.some(o => o.value === option.value)) {
      const values = selected.value.filter(o => o.value !== option.value)
      // Cant remove the last value if it's required
      if ((required && values.length > 0) || !required) {
        selected.value = values.length > 0 ? values : undefined
      }
    }
    else {
      if (!selected.value) {
        selected.value = [option]
      }
      else if (!maxActiveOptions || (selected.value.length < maxActiveOptions)) {
        selected.value?.push(option)
      }
    }
  }
}

// Search
const searchStr = ref()
const filteredOptions = computed(() => {
  if (!searchStr.value || searchStr.value.length === 0)
    return options

  return options.filter((option) => {
    return searchString(option.label, searchStr.value)
  })
})

// Render text inside the button
const renderPlaceholder = computed(() => {
  // Render placeholder or a default select option
  if (!selected.value || selected.value.length === 0)
    return placeholder ?? 'Select an option'

  // Selected values
  if (single)
    return selected.value[0].label

  // If amount of selected exceeds the active capacity
  if (selected.value.length > 3) {
    return `${selected.value.length} selected`
  }

  // Just list ALL selected optionsx
  return selected.value.map(o => o.label).join(', ')
})

onMounted(() => {
  if (readonly && showClear) {
    console.warn('[Select] readonly and showClear are mutually exclusive props. The clear button will not show up if required is present.')
  }
})

const dropdownRef = useTemplateRef('dropdown')

function clearValue() {
  selected.value = undefined
  dropdownRef.value?.close()
}

const id = useId()
</script>

<template>
  <div class="vui-input-container vui-select" :class="{ required, readonly, disabled, 'has-errors': errors.length > 0 }" :style="sizeStyle">
    <Dropdown
      ref="dropdown"
      :max-height
      :transition-name
      :no-mobile-drawer="noMobileDrawer"
      @close="triggerRef?.focus({ preventScroll: true })"
    >
      <template #trigger="{ toggle, isOpen }">
        <div class="vui-input vui-select-trigger-content">
          <label v-if="label" :for="id">{{ label }}</label>
          <p v-if="hint" class="vui-input-hint">
            {{ hint }}
          </p>

          <button
            :id
            ref="trigger"
            class="vui-input-style vui-select-trigger-container"
            :class="{ 'has-value': selected && selected.length > 0 }"
            :disabled
            @click="toggle"
          >
            <span>
              {{ renderPlaceholder }}
            </span>
            <template v-if="showClear && !required && (selected && selected.length > 0)">
              <div class="flex-1" />
              <Button
                plain
                square
                size="s"
                @click.stop="clearValue"
              >
                <IconX />
              </Button>
            </template>
            <IconCaretUp v-if="isOpen" />
            <IconCaretDown v-else />
          </button>
        </div>
      </template>

      <template #default="{ close, isOpen }">
        <DropdownTitle v-if="search" sticky>
          <Input
            v-model="searchStr"
            placeholder="Search..."
            :focus="isOpen && !viewport.s"
            expand
          >
            <template #start>
              <IconMagnifyingGlass />
            </template>
          </Input>
        </DropdownTitle>

        <p v-if="filteredOptions.length === 0" class="vue-select-no-results">
          No results...
        </p>

        <DropdownItem
          v-for="option in filteredOptions"
          :key="option.value"
          :class="{ selected: selected?.find(v => v.value === option.value) }"
          @click="() => {
            if (readonly) return

            setValue(option)
            // In single mode, close modal after clicking
            if (single && !(required && selected && selected[0].value === option.value)) {
              close()
            }
          }"
        >
          {{ option.label }}
        </DropdownItem>
      </template>
    </Dropdown>

    <p v-if="maxActiveOptions && !single" class="vui-input-limit">
      {{ `${selected ? selected.length : 0}/${maxActiveOptions}` }}
    </p>
    <ul v-if="errors.length > 0" class="vui-input-errors">
      <li v-for="err in errors" :key="err">
        {{ err }}
      </li>
    </ul>
  </div>
</template>
